import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { ComponentStore } from '@ngrx/component-store';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom, tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { combineLatest, EMPTY, empty, Observable, of, timer } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { PublisherViewModel } from 'src/app/models/publisher.model';
import { School } from 'src/app/models/school.model';
import { Slip } from 'src/app/models/slip.model';
import { isQuestion, ItemChoice, ServiceError, SlipDto, SlipService, SlipStatus } from 'src/app/services/slip.service';
import { applyTimer } from 'src/app/services/timer';
import * as appVersionActions from 'src/app/store/actions/app-version.action';
import * as fromStore from '../../store';
import * as slipPaymentIntentActions from '../../store/actions/slip-payment-intent.action';
import * as slipPaymentIntentSelectors from '../../store/selectors/slip-payment-intent.selector';
import { StudentSlipItemChoicesModalComponent } from './student-slip-item-choices-modal/student-slip-item-choices-modal.component';
import { StudentSlipQuestionsSummaryComponent } from './student-slip-questions-summary/student-slip-questions-summary.component';
import { StudentSlipQuestionsWizardComponent } from './student-slip-questions-wizard/student-slip-questions-wizard.component';

export type ComponentStatus = 'pending' | 'in-progress' | 'done' | 'restricted' | 'void' | 'expired' | 'declined';
export type ComponentType = 'consent' | 'payment' | 'questions' | 'item-choices';
export type SlipType = 'trip' | 'consent' | 'item' | 'slip';

export enum PaymentState {
  Initialised,
  ProcessingPayment,
  ConfirmingPayment,
  PaymentConfirmed
}
export interface PaymentStatus {
  inProgress: boolean;
  intentId: number;
  method: 'google' | 'apple' | 'card';
  confirmed?: boolean;
}

export interface ConsentStatus {
  inProgress: boolean;
  error: string;
  showReload: boolean;
}

export interface ConsentInfo {
  question: string;
  canDecline: boolean;
  mandatory: boolean;
  enableDeclineWarning: boolean;
  status: ComponentStatus;
  response: {
    accepted: boolean;
  };
  progress: {
    working: boolean;
    error: string;
    showReload: boolean;
  };
}

export interface PaymentInfo {
  due: number;
  balance: number;
  status: ComponentStatus;
  minimumAmount: number;
  paymentKey: string;
  paymentMethodId: string;
}

export interface QuestionsInfo {
  status: ComponentStatus;
  redacted: boolean;
}

export interface ItemChoicesComponentInfo {
  componentId: number;
  choices: ItemChoice[];
  due: number;
  balance?: number;
  status: ComponentStatus;
  minimumAmount: number;
  paymentKey: string;
  paymentMethodId: string;
}

export interface SlipInfo {
  schoolId: number;
  schoolName: string;
  studentId: number;
  slipId: number;
  title: string;
}

export interface SlipDetails {
  title: string;
  description: string;
  createdOn: string;
  isOpen: boolean;
  type: string;
  componentStatus: ComponentStatus;
  dueOn: {
    date: string;
    overdue: boolean;
  };
  cost: number;
  publisher: {
    data: string;
    displayName: string;
    initials: string;
  };
}

export interface ViewModel {
  slipInfo: SlipInfo;
  slipDetails: SlipDetails;
  loadingProgress: {
    working: boolean;
    error: string;
  };
  paymentInfo: PaymentInfo;
  consentInfo: ConsentInfo;
  questionsInfo: QuestionsInfo;
  itemChoicesInfo: ItemChoicesComponentInfo;
  paymentProgress: PaymentProgress;
}

export interface PaymentProgress {
  paymentStatus: PaymentStatus;
  state: PaymentState;
}

export interface StudentSlipModalState {
  schoolId: number;
  studentId: number;
  slip: Slip;
  dueOn: {
    date: string;
    overdue: boolean;
  };
  payNowAmount: number;
  loadingProgress: {
    working: boolean;
    error: string;
  };
  paymentStatus: PaymentStatus;
  consentStatus: ConsentStatus;
}

export type ErrorLut<T> = Record<string, T>;

export function findError<T>(code: number, lut: ErrorLut<T>, notFoundError: T): T {
  return lut[code] ?? notFoundError;
}

export const slipLoadErrorLut: ErrorLut<string> = {
  0: `We weren't able to load the slip because it looks like you're offline. <br /><br />Please check your wifi or mobile data connection and try again later.`,
  404: `This slip is no longer active for your child.<br /><br />Please contact the school for more information.`
};

export const consentSubmitErrorLut: ErrorLut<{ message: string; showReload: boolean }> = {
  0: {
    message: `We weren't able to submit your response because it looks like you're offline. <br /><br />Please check your wifi or mobile data connection and try again later.`,
    showReload: false
  },
  404: {
    message: `This slip is no longer active for your child.<br /><br />Please contact the school for more information.`,
    showReload: false
  },
  2000: {
    message: `This slip is no longer active for your child.<br /><br />Please contact the school for more information.`,
    showReload: false
  },
  2001: {
    message: `This slip is no longer active for your child.<br /><br />Please contact the school for more information.`,
    showReload: false
  },
  2007: {
    message: `A response for this consent request has already been submitted.<br /><br />Please tap 'Refresh slip' to view.`,
    showReload: true
  }
};

@Injectable()
export class StudentSlipModalStore extends ComponentStore<StudentSlipModalState> {
  constructor(
    private store: Store,
    private actions: Actions,
    private slipService: SlipService,
    private modalCtrl: ModalController
  ) {
    super({
      schoolId: null,
      studentId: null,
      slip: null,
      dueOn: null,
      payNowAmount: null,
      loadingProgress: null,
      paymentStatus: null,
      consentStatus: null
    });
  }
  readonly schoolId$ = this.select(this.state$, state => state.schoolId);
  readonly school$ = this.schoolId$.pipe(
    mergeMap(id => of(id).pipe(withLatestFrom(this.store.select(fromStore.getSchool(id))))),
    map(([id, school]) => school)
  );
  readonly studentId$ = this.select(this.state$, state => state.studentId);
  readonly student$ = this.studentId$.pipe(
    mergeMap(id => of(id).pipe(withLatestFrom(this.store.select(fromStore.getStudent(id))))),
    map(([id, student]) => student)
  );
  readonly publisher$ = this.state$.pipe(
    mergeMap(state =>
      of(state.slip?.publisher.id).pipe(
        withLatestFrom(this.store.select(fromStore.getPublisherViewModel(state.slip?.publisher.id)))
      )
    ),
    map(([id, publisher]) => publisher)
  );
  readonly paymentMethodId$ = this.store.select(fromStore.getPaymentMethodId);
  readonly vm$ = this.select(
    this.state$.pipe(tap(console.log)),
    this.school$,
    this.paymentMethodId$,
    this.publisher$,
    (state: StudentSlipModalState, school: School, paymentMethodId: string, publisher: PublisherViewModel) => {
      // Only enable decline warning if a non consent component present also
      const consentInfo = state.slip?.consent
        ? {
            question: state.slip.consent.approvalWording,
            canDecline: state.slip.consent.canDecline,
            mandatory: state.slip.consent.isRequired,
            enableDeclineWarning: state.slip.consent.isRequired && (!!state.slip.questions || !!state.slip.payment),
            status: state.slip.consent.componentStatus,
            response: state.slip.consent.response
              ? {
                  accepted: state.slip.consent.response.granted
                }
              : null,
            progress: state.consentStatus
              ? {
                  working: state.consentStatus.inProgress,
                  error: state.consentStatus.error,
                  showReload: state.consentStatus.showReload
                }
              : null
          }
        : null;

      const questionsInfo = state.slip?.questions
        ? {
            status: state.slip.questions.componentStatus,
            redacted: state.slip.questions.redacted
          }
        : null;
      const itemChoicesInfo = state.slip?.itemChoicesComponent
        ? {
            componentId: state.slip.itemChoicesComponent.componentId,
            status: state.slip.itemChoicesComponent.componentStatus,
            choices: state.slip.itemChoicesComponent.responsePayload?.itemChoices,
            due: state.slip.itemChoicesComponent.responsePayload?.itemChoices.reduce((accum, item) => {
              return accum + (item.unitCost ?? 0) * item.quantity;
            }, 0),
            balance: state.slip.balance.amount,
            minimumAmount: state.slip.itemChoicesComponent.payload.minimumAmount,
            paymentKey: school.paymentKey,
            paymentMethodId: paymentMethodId
          }
        : null;

      const paymentInfo = state.slip?.payment
        ? {
            due: state.slip.balance.due,
            balance: state.slip.balance.amount,
            status: state.slip.payment.componentStatus,
            minimumAmount: state.slip.payment.minimumAmount,
            paymentKey: school.paymentKey,
            paymentMethodId: paymentMethodId
          }
        : null;

      const slipInfo = state.slip?.supported
        ? {
            schoolId: school.id,
            schoolName: school.name,
            studentId: state.studentId,
            slipId: state.slip.id,
            title: state.slip.title
          }
        : null;

      const slipDetails = state.slip?.supported
        ? {
            title: state.slip.title,
            description: state.slip.description?.length > 0 ? state.slip.description : '-',
            // description:
            //   'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?',
            createdOn: state.slip.createdOn,
            dueOn: state.dueOn,
            cost: state.slip.balance?.due,
            isOpen: state.slip.slipStatus === SlipStatus.Active,
            type: state.slip.category,
            componentStatus: state.slip?.slipComponentStatus,
            publisher: publisher
              ? {
                  data: publisher.avatarBase64,
                  displayName: publisher.displayName,
                  initials: getInitials(publisher.forename, publisher.surname)
                }
              : null
          }
        : null;
      return {
        slipInfo: slipInfo,
        slipDetails: slipDetails,
        consentInfo: consentInfo,
        questionsInfo: questionsInfo,
        itemChoicesInfo: itemChoicesInfo,
        paymentInfo: paymentInfo,
        paymentProgress: {
          paymentStatus: state.paymentStatus,
          state: !state.paymentStatus
            ? PaymentState.Initialised
            : state.paymentStatus.inProgress
              ? PaymentState.ProcessingPayment
              : state.paymentStatus.confirmed
                ? PaymentState.PaymentConfirmed
                : PaymentState.ConfirmingPayment
        },
        loadingProgress: state.loadingProgress
      } as ViewModel;
    }
  ).pipe(tap(vm => console.log('Slip modal store vm', vm)));

  readonly setSchoolId = this.updater((state, value: number) => ({
    ...state,
    schoolId: value
  }));
  readonly setStudentId = this.updater((state, value: number) => ({
    ...state,
    studentId: value
  }));
  readonly setPaymentStatus = this.updater((state, value: PaymentStatus) => ({
    ...state,
    paymentStatus: value
  }));
  readonly setConsentStatus = this.updater((state, value: ConsentStatus) => ({
    ...state,
    consentStatus: value
  }));
  readonly setLoadingProgress = this.updater((state, value: { working: boolean; error: string }) => ({
    ...state,
    loadingProgress: value
  }));
  readonly setSlip = this.updater((state, value: Slip) => {
    return {
      ...state,
      loadingProgress: null,
      slip: value,
      dueOn: value.dueOn,
      payNowAmount: value.payment ? value.balance.amount : null
    };
  });

  readonly loadSlip = this.effect((params$: Observable<{ id: number; timeout?: number }>) => {
    return params$.pipe(
      switchMap((params: { id: number; timeout?: number }) => {
        this.setLoadingProgress({ working: true, error: null });

        return combineLatest([
          timer(params.timeout ?? 1000),
          this.slipService.getSlip(this.get().schoolId, this.get().studentId, params.id).pipe(
            catchError((errorResponse) => {
              const message = findError(
                errorResponse.error?.ErrorCode ?? errorResponse.status,
                slipLoadErrorLut,
                `We weren't able to load the slip. <br /><br />Please try again later.`
              );
              return of(message);
            })
          )
        ]).pipe(
          tapResponse(
            ([_, result]) => {
              if (typeof result === 'string') {
                this.setLoadingProgress({ working: false, error: result });
              } else {
                this.setSlip(Slip.create(result));
              }
            },
            () => {
              return EMPTY;
            }
          )
        );
      })
    );
  });

  // Prime slip that is pending confirmation
  readonly initialisePaymentStatus = this.effect((params$: Observable<{ slipId: number; studentId: number }>) => {
    return params$.pipe(
      switchMap((params: { slipId: number; studentId: number }) =>
        of(empty).pipe(
          withLatestFrom(
            this.store.select(
              slipPaymentIntentSelectors.selectSlipPaymentIntentBySlipStudent(params.slipId, params.studentId)
            )
          )
        )
      ),
      tap(([, intent]) => {
        if (intent) {
          this.setPaymentStatus({
            inProgress: false,
            method: intent.method,
            intentId: intent.id
          });
        }
      })
    );
  });

  // We check for payment confirmation in the background through the store
  readonly monitorIntentCheckSuccess = this.effect((trigger$) => {
    return trigger$.pipe(
      switchMap(() => {
        return this.actions.pipe(
          ofType(slipPaymentIntentActions.checkIntentSuccess),
          tapResponse(
            (result) => {
              if (result.id === this.get().paymentStatus?.intentId) {
                this.setSlip(Slip.create(result.slip));
                this.setPaymentStatus({ ...this.get().paymentStatus, confirmed: true });
              }
            },
            () => EMPTY
          )
        );
      })
    );
  });

  // Simulate submitConsentResponse flow...
  // readonly submitConsentResponse = this.effect(
  //   (params$: Observable<{ payload: { accepted: boolean }; timeout?: number }>) => {
  //     return params$.pipe(
  //       tap((params) => {
  //         this.setConsentStatus({ inProgress: true, error: null, showReload: false });
  //       }),
  //       delay(2000),
  //       tap((params: { payload: { accepted: boolean }; timeout?: number }) => {
  //         let slip = this.get().slip;
  //         const componentId = slip.milestones[0].components.find((c) => c.type === ConsentV1ComponentType).id;
  //         slip.componentResponses[0] = {
  //           id: 97,
  //           componentId: componentId,
  //           personId: 33286,
  //           response: `{"answer": "${params.payload.accepted ? 'granted' : 'declined'}"}`
  //         };

  //         this.setSlip(slip);
  //         this.setConsentStatus({
  //           inProgress: false,
  //           error: null,
  //           showReload: false
  //         });
  //       })
  //     );
  //   }
  // );

  readonly submitConsentResponse = this.effect(
    (params$: Observable<{ payload: { accepted: boolean }; timeout?: number }>) => {
      return params$.pipe(
        switchMap((params: { payload: { accepted: boolean }; timeout?: number }) => {
          this.setConsentStatus({ inProgress: true, error: null, showReload: false });

          const apiCall = this.slipService.submitComponentResponse(
            this.get().schoolId,
            this.get().studentId,
            this.get().slip.id,
            this.get().slip.consent.componentId,
            { answer: params.payload.accepted ? 'granted' : 'declined' }
          );

          return applyTimer(apiCall, params.timeout).pipe(
            tapResponse(
              (result: SlipDto) => {
                this.setConsentStatus({
                  inProgress: false,
                  error: typeof result === 'string' ? result : null,
                  showReload: false
                });
                this.setSlip(Slip.create(result));
              },
              (error: ServiceError) => {
                if (error.stage === 0) {
                  const messageDetail = findError(
                    error.apiError?.ErrorCode ?? error.httpStatus,
                    consentSubmitErrorLut,
                    {
                      message: `We weren't able to submit your response. <br /><br />Please try again later.`,
                      showReload: false
                    }
                  );
                  // Submit failed
                  this.setConsentStatus({
                    inProgress: false,
                    error: messageDetail.message,
                    showReload: messageDetail.showReload
                  });
                } else {
                  // Submit success but slip load failed
                  this.loadSlip({ id: this.get().slip.id });
                }
              }
            )
          );
        })
      );
    }
  );

  readonly updatePaymentMethod = this.effect((params$: Observable<{ methodId: string }>) => {
    return params$.pipe(
      tap((params: { methodId: string }) => {
        this.store.dispatch(new fromStore.UpdatePaymentMethodId({ paymentMethodId: params.methodId }));
      })
    );
  });
  readonly visitAppStore = this.effect((trigger$) => {
    return trigger$.pipe(
      tap(() => {
        this.store.dispatch(appVersionActions.visitAppStore());
      })
    );
  });
  readonly dismissModal = this.effect((trigger$) => {
    return trigger$.pipe(
      tap(() => {
        this.modalCtrl.dismiss({ slip: this.get().slip });
      })
    );
  });

  readonly editQuestionResponses = this.effect((trigger$) => {
    return trigger$.pipe(
      tap(async () => {
        const modal = await this.modalCtrl.create({
          component: StudentSlipQuestionsWizardComponent,
          componentProps: {
            items: this.get().slip.questions.items,
            // items: [
            //   {
            //     questionWording: 'text',
            //     flexibility: 'optional',
            //     answerType: 'text'
            //   },
            //   {
            //     questionWording: 'tel number',
            //     flexibility: 'optional',
            //     answerType: 'phone'
            //   },
            //   {
            //     questionWording: 'multi cjoice',
            //     flexibility: 'optional',
            //     answerType: 'multiple-choice',
            //     options: {
            //       answerOptions: ['Option A', 'Option B'],
            //       hasOther: true,
            //       isMultiSelect: false
            //     }
            //   }
            // ],
            slipComponentInfo: {
              schoolId: this.get().schoolId,
              studentId: this.get().studentId,
              slipId: this.get().slip.id,
              componentId: this.get().slip.questions.componentId
            }
          },
          backdropDismiss: false
        });
        await modal.present();
        const { data } = await modal.onWillDismiss();
        if (data?.slip) {
          this.setSlip(Slip.create(data.slip));
        } else if (data?.reloadSlip) {
          this.loadSlip({ id: this.get().slip.id });
        }
      })
    );
  });

  readonly viewQuestionResponses = this.effect((trigger$) => {
    return trigger$.pipe(
      withLatestFrom(this.vm$),
      tap(async ([, vm]) => {
        const items = this.get()
          .slip.questions.items.map((q, index) => ({
            index: index,
            question: q
          }))
          .filter(i => isQuestion(i.question))
          .map(i => ({
            question: i.question,
            response: this.get().slip.questions.answers ? this.get().slip.questions.answers[i.index] : null
          }));
        const modal = await this.modalCtrl.create({
          component: StudentSlipQuestionsSummaryComponent,
          componentProps: {
            redacted: this.get().slip.questions.redacted,
            status: vm.questionsInfo.status,
            items: items
          },
          backdropDismiss: false
        });
        await modal.present();
      })
    );
  });

  readonly showItemChoicesModal = this.effect((params$: Observable<{ readonly: boolean }>) => {
    return params$.pipe(
      concatLatestFrom(x => this.school$),
      tap(async ([params, school]) => {
        const modal = await this.modalCtrl.create({
          component: StudentSlipItemChoicesModalComponent,
          componentProps: {
            itemChoices: this.get().slip.itemChoicesComponent,

            // itemChoices: testItemChoices,

            slipComponentInfo: {
              schoolId: this.get().schoolId,
              schoolName: school.name,
              studentId: this.get().studentId,
              slipId: this.get().slip.id,
              componentId: this.get().slip.itemChoicesComponent.componentId,
              title: this.get().slip.title
            },
            slipModalStore: this,
            readonly: params.readonly
          },
          backdropDismiss: false
        });
        await modal.present();
        const { data } = await modal.onWillDismiss();
        if (data?.itemUpdates) {
          const updatedSlip = {
            ...this.get().slip,
            itemChoices: { ...this.get().slip.itemChoicesComponent, items: data.itemUpdates }
          };
          this.setSlip(updatedSlip);
        }
        if (data?.slip) {
          this.setSlip(Slip.create(data.slip));
        }
        if (data?.reloadSlip) {
          this.loadSlip({ id: this.get().slip.id });
        }
      })
    );
  });
}

export function getInitials(forename: string, surname: string): string {
  return forename.charAt(0).toUpperCase() + surname.charAt(0).toUpperCase();
}

const fixedItemChoices = {
  componentId: 1,
  componentStatus: 'pending',
  payload: {
    description: 'Optional instructions or question wording goes here',
    items: [
      {
        id: 1,
        itemType: 'item',
        itemProperties: {
          name: 'Item name goes here',
          description:
            'item based, multi choice with limited inventory and pricing aaa bbb ccc ddd eee fff ggg hhh iii jjj kkk lll mmm nnn ooo ppp qqq rrr sss ttt uuu vvv www xxx yyy zzz'
        },
        choiceLimit: 3,
        choiceType: 'multiple',
        inventory: [
          { id: 1, name: 'Thing 1', capacity: 20, consumed: 2 },
          { id: 2, name: 'Thing 2', capacity: 11, consumed: 10 }
        ],
        choices: [
          {
            id: 1,
            name: 'Choice 1 name',
            description:
              'Choice 1 description aaa bbb ccc ddd eee fff ggg hhh iii jjj kkk lll mmm nnn ooo ppp qqq rrr sss ttt uuu vvv www xxx yyy zzz',
            choiceLimit: 3,
            inventoryIds: [1],
            pricing: { default: 250 }
          },
          {
            id: 2,
            name: 'Choice 2 name',
            choiceLimit: 2,
            inventoryIds: [2],
            pricing: { default: 0 }
          },
          {
            id: 3,
            name: 'Choice 3 name',
            pricing: { default: 0 }
          }
        ]
      },
      {
        id: 2,
        itemType: 'item',
        itemProperties: {
          name: 'Item name goes here',
          description: 'item based, multi choice with limited inventory and no pricing'
        },
        choiceLimit: 3,
        choiceType: 'multiple',
        inventory: [
          { id: 3, name: 'Thing 1', capacity: 20, consumed: 2 },
          { id: 4, name: 'Thing 2', capacity: 11, consumed: 10 }
        ],
        choices: [
          {
            id: 4,
            name: 'Choice 1 name',
            description: 'Choice 1 description',
            choiceLimit: 3,
            inventoryIds: [3]
          },
          {
            id: 5,
            name: 'Choice 2 name',
            choiceLimit: 2,
            inventoryIds: [4]
          }
        ]
      },
      {
        id: 3,
        itemType: 'event',
        itemProperties: {
          date: '2025-06-06',
          startTime: '15:30',
          endTime: '16:30',
          location: 'Sports Hall',
          description: 'event based single choice with limited inventory and pricing'
        },
        choiceLimit: 1,
        choiceType: 'single',
        inventory: [
          { id: 5, name: 'Thing 1', capacity: 25, consumed: 3 },
          { id: 6, name: 'Thing 2', capacity: 15, consumed: 2 }
        ],
        choices: [
          {
            id: 6,
            name: 'Choice 1 name',
            description: 'Choice 1 description',
            choiceLimit: 1,
            inventoryIds: [5],
            pricing: { default: 250 }
          },
          {
            id: 7,
            name: 'Choice 2 name',
            choiceLimit: 1,
            inventoryIds: [6],
            pricing: { default: 350 }
          }
        ]
      },
      {
        id: 4,
        itemType: 'event',
        itemProperties: {
          date: '2025-06-06',
          startTime: '15:30',
          endTime: '16:30',
          location: 'Sports Hall',
          description: 'event based single choice with unlimited inventory and pricing'
        },
        choiceLimit: 1,
        choiceType: 'single',
        choices: [
          {
            id: 8,
            choiceLimit: 1,
            pricing: { default: 250 }
          }
        ]
      },
      {
        id: 5,
        itemType: 'event',
        itemProperties: {
          date: '2025-06-06',
          startTime: '15:30',
          endTime: '16:30',
          location: 'Sports Hall',
          description: 'event based single choice with limited inventory and pricing'
        },
        choiceLimit: 1,
        choiceType: 'single',
        inventory: [{ id: 7, name: 'Thing 2', capacity: 15, consumed: 2 }],
        choices: [
          {
            id: 9,
            choiceLimit: 1,
            inventoryIds: [7],
            pricing: { default: 250 }
          }
        ]
      }
    ]
  },
  responsePayload: {}
};

const selectionModeVariations = {
  componentId: 1,
  componentStatus: 'pending',
  payload: {
    description: 'Optional instructions or question wording goes here',
    items: [
      {
        id: 1,
        itemType: 'item',
        itemProperties: {
          name: 'Item name goes here',
          description: 'single choice type with item choiceLimit: 3'
        },
        choiceLimit: 3,
        choiceType: 'single',
        inventory: [{ id: 1, name: 'Thing 1', capacity: 20, consumed: 2 }],
        choices: [
          {
            id: 1,
            name: 'Choice 1 name',
            description:
              'Choice 1 description aaa bbb ccc ddd eee fff ggg hhh iii jjj kkk lll mmm nnn ooo ppp qqq rrr sss ttt uuu vvv www xxx yyy zzz',
            choiceLimit: 3,
            inventoryIds: [1],
            pricing: { default: 250 }
          }
        ]
      },
      {
        id: 2,
        itemType: 'item',
        itemProperties: {
          name: 'Item name goes here',
          description: 'single choice type with item choiceLimit: 1'
        },
        choiceLimit: 1,
        choiceType: 'single',
        inventory: [{ id: 2, name: 'Thing 1', capacity: 20, consumed: 2 }],
        choices: [
          {
            id: 2,
            name: 'Choice 2 name',
            description:
              'Choice 1 description aaa bbb ccc ddd eee fff ggg hhh iii jjj kkk lll mmm nnn ooo ppp qqq rrr sss ttt uuu vvv www xxx yyy zzz',
            choiceLimit: 1,
            inventoryIds: [2],
            pricing: { default: 250 }
          }
        ]
      },
      {
        id: 3,
        itemType: 'item',
        itemProperties: {
          name: 'Item name goes here',
          description: 'multiple choice type with item choiceLimit: 3'
        },
        choiceLimit: 3,
        choiceType: 'multiple',
        inventory: [
          { id: 3, name: 'Thing 1', capacity: 20, consumed: 2 },
          { id: 4, name: 'Thing 2', capacity: 11, consumed: 10 }
        ],
        choices: [
          {
            id: 4,
            name: 'Choice 4 name',
            description: 'Choice 1 description',
            choiceLimit: 3,
            inventoryIds: [3]
          },
          {
            id: 5,
            name: 'Choice 5 name',
            choiceLimit: 2,
            inventoryIds: [4]
          }
        ]
      },
      {
        id: 4,
        itemType: 'item',
        itemProperties: {
          name: 'Item name goes here',
          description: 'multiple choice type with item choiceLimit: 1'
        },
        choiceLimit: 1,
        choiceType: 'multiple',
        inventory: [
          { id: 5, name: 'Thing 1', capacity: 20, consumed: 2 },
          { id: 6, name: 'Thing 2', capacity: 11, consumed: 10 }
        ],
        choices: [
          {
            id: 5,
            name: 'Choice 5 name',
            description: 'Choice 5 description',
            choiceLimit: 1,
            inventoryIds: [5]
          },
          {
            id: 6,
            name: 'Choice 6 name',
            choiceLimit: 1,
            inventoryIds: [6]
          }
        ]
      },
      {
        id: 5,
        itemType: 'item',
        itemProperties: {
          name: 'Item name goes here',
          description: 'multiple-single-choice choice type with item choiceLimit: 3'
        },
        choiceLimit: 3,
        choiceType: 'multiple-single-choice',
        inventory: [
          { id: 7, name: 'Thing 1', capacity: 20, consumed: 2 },
          { id: 8, name: 'Thing 2', capacity: 11, consumed: 10 }
        ],
        choices: [
          {
            id: 7,
            name: 'Choice 7 name',
            description: 'Choice 7 description',
            choiceLimit: 3,
            inventoryIds: [7]
          },
          {
            id: 8,
            name: 'Choice 8 name',
            choiceLimit: 2,
            inventoryIds: [8]
          }
        ]
      },
      {
        id: 6,
        itemType: 'item',
        itemProperties: {
          name: 'Item name goes here',
          description: 'multiple-single-choice choice type with item choiceLimit: 1'
        },
        choiceLimit: 1,
        choiceType: 'multiple',
        inventory: [
          { id: 9, name: 'Thing 1', capacity: 20, consumed: 2 },
          { id: 10, name: 'Thing 2', capacity: 11, consumed: 10 }
        ],
        choices: [
          {
            id: 9,
            name: 'Choice 9 name',
            description: 'Choice 9 description',
            choiceLimit: 1,
            inventoryIds: [9]
          },
          {
            id: 10,
            name: 'Choice 10 name',
            choiceLimit: 1,
            inventoryIds: [10]
          }
        ]
      }
    ]
  },
  responsePayload: {}
};

const breakfastClubItemChoices = {
  componentId: 1,
  componentStatus: 'pending',
  payload: {
    description: 'Breakfast club example',
    items: [
      {
        id: 2,
        itemType: 'event',
        itemProperties: {
          date: '2025-06-06',
          startTime: '07:00',
          endTime: '09:00',
          location: 'Food Hall',
          description: 'xxx'
        },
        choiceLimit: 1,
        description: 'Book your places',
        inventoryBehaviour: 'split-ascending',
        inventory: [
          { id: 1, name: 'Space', capacity: 2, consumed: 0, next: 2 },
          { id: 2, name: 'Space', capacity: 3, consumed: 0, prev: 1, next: 3 },
          { id: 3, name: 'Space', capacity: 5, consumed: 0, prev: 2, next: 4 },
          { id: 4, name: 'Space', capacity: 10, consumed: 0, prev: 3 }
        ],
        choices: [
          {
            id: 1,
            name: 'Early slot',
            startTime: '07:00',
            endTime: '09:00',
            choiceLimit: 1,
            inventoryIds: [1]
          },
          {
            id: 2,
            startTime: '07:30',
            endTime: '09:00',
            choiceLimit: 1,
            inventoryIds: [2]
          },
          {
            id: 3,
            startTime: '08:00',
            endTime: '09:00',
            choiceLimit: 1,
            inventoryIds: [3]
          },
          {
            id: 4,
            name: 'Late slot',
            startTime: '08:30',
            endTime: '09:00',
            choiceLimit: 1,
            inventoryIds: [4],
            pricing: {
              default: 300
            }
          }
        ]
      }
    ]
  },
  responsePayload: {}
};

const testItemChoices = {
  componentId: 1,
  componentStatus: 'pending',
  payload: {
    description: 'test',
    items: [
      {
        id: 1,
        itemType: 'item',
        itemProperties: {
          name: 'Item name goes here',
          description:
            'item based, multi choice with limited inventory and pricing aaa bbb ccc ddd eee fff ggg hhh iii jjj kkk lll mmm nnn ooo ppp qqq rrr sss ttt uuu vvv www xxx yyy zzz'
        },
        choiceLimit: null,
        choiceType: 'multiple',
        inventory: [
          { id: 1, name: 'Thing 1', capacity: null, consumed: 0, type: 'auto' },
          { id: 2, name: 'Thing 2', capacity: null, consumed: 0, type: 'auto' },
          { id: 3, name: 'Thing 3', capacity: 7, consumed: 0, type: 'manual' },
          { id: 4, name: 'Thing 4', capacity: 6, consumed: 0, type: 'manual' }
        ],
        choices: [
          {
            id: 1,
            name: 'Choice 1 name',
            description:
              'Choice 1 description aaa bbb ccc ddd eee fff ggg hhh iii jjj kkk lll mmm nnn ooo ppp qqq rrr sss ttt uuu vvv www xxx yyy zzz',
            choiceLimit: 5,
            inventoryIds: [1, 3, 4],
            pricing: { default: 250 }
          },
          {
            id: 2,
            name: 'Choice 2 name',
            choiceLimit: 5,
            inventoryIds: [2, 3],
            pricing: { default: 0 }
          },
          {
            id: 3,
            name: 'Choice 3 name',
            choiceLimit: 5,
            inventoryIds: [2, 4],
            pricing: { default: 0 }
          }
        ]
      }
    ]
  },
  responsePayload: {}
};
